/** AI 助手 ↔ iframe（franchisee）通信协议，见 docs/iframe-bridge.md */

export const AI_BRIDGE_SOURCE = {
  ASSISTANT: 'aiMyAssistant',
  FRANCHISEE: 'franchisee',
} as const;

export const AI_BRIDGE_TYPE = {
  PING: 'AI_ASSISTANT/PING',
  ROUTE: 'AI_ASSISTANT/ROUTE',
  PONG: 'AI_ASSISTANT/PONG',
  /** 业务操作指令（不负责跳转，写入全局 store 后由页面监听消费） */
  ACTION: 'AI_ASSISTANT/ACTION',
  /** 父 → iframe：查询当前内部路由（助手短路用） */
  GET_ROUTE: 'AI_ASSISTANT/GET_ROUTE',
  /** iframe → 父：响应当前内部路由 */
  ROUTE_STATE: 'AI_ASSISTANT/ROUTE_STATE',
} as const;

/** ACTION.payload.action 业务常量，与父页协议保持一致 */
export const AI_BRIDGE_ACTION = {
  /** 潜商缓冲池·暂缓推进：AI 侧重启后通知 iframe 刷新列表 */
  POTENTIAL_BUFFER_HOLD_REOPEN: 'POTENTIAL_BUFFER_HOLD_REOPEN',
} as const;

export type AiBridgeSource = (typeof AI_BRIDGE_SOURCE)[keyof typeof AI_BRIDGE_SOURCE];
export type AiBridgeType = (typeof AI_BRIDGE_TYPE)[keyof typeof AI_BRIDGE_TYPE];
export type AiBridgeActionName =
  (typeof AI_BRIDGE_ACTION)[keyof typeof AI_BRIDGE_ACTION];

/** ROUTE 结构化载荷（保留 content 兼容旧助手） */
export type AiRoutePayload = {
  path: string;
  /** @deprecated 兼容旧字段；解析时优先 path，回退 content */
  content?: string;
  from?: string;
  ts?: number;
  /** 一期仅打日志预留，不执行业务 */
  action?: string;
  params?: Record<string, string>;
};

/** ACTION 结构化载荷（与 ROUTE 解耦） */
export type AiActionPayload = {
  action: string;
  params?: Record<string, unknown>;
  from?: string;
  ts?: number;
};

/** GET_ROUTE 请求载荷 */
export type AiGetRoutePayload = {
  from?: string;
  ts?: number;
  /** 可选；有则 ROUTE_STATE 原样回传，便于并发配对 */
  requestId?: string;
};

/** ROUTE_STATE 响应载荷（path 形态须与 ROUTE 一致） */
export type AiRouteStatePayload = {
  path: string;
  pathname?: string;
  search?: string;
  href?: string;
  requestId?: string;
  at?: number;
};

export interface AiBridgeMessage {
  source: string;
  type: string;
  payload?: any;
}

export function isAiBridgeMessage(data: any): data is AiBridgeMessage {
  return !!(data && typeof data === 'object' && typeof data.source === 'string' && typeof data.type === 'string');
}

/** 文档示例白名单；实际按 hostname 后缀放宽到 *.hwwt2.com */
export const AI_BRIDGE_DEFAULT_ORIGINS = [
  'https://uat-ifd.hwwt2.com',
  'https://ifd.hwwt2.com',
] as const;

export function isAllowedAiBridgeOrigin(origin: string): boolean {
  if (!origin || origin === 'null') {
    return false;
  }
  if ((AI_BRIDGE_DEFAULT_ORIGINS as readonly string[]).includes(origin)) {
    return true;
  }
  try {
    const { hostname, protocol } = new URL(origin);
    if (protocol !== 'https:' && protocol !== 'http:') {
      return false;
    }
    return hostname === 'hwwt2.com' || hostname.endsWith('.hwwt2.com');
  } catch {
    return false;
  }
}

/**
 * 从 ROUTE payload 解析规范化路由对象。
 * path 优先，回退 content；无有效路径时返回 null。
 */
export function parseRoutePayload(payload: unknown): AiRoutePayload | null {
  if (payload == null) {
    return null;
  }
  if (typeof payload === 'string') {
    const path = payload.trim();
    if (!path) {
      return null;
    }
    return {
      path,
      content: path,
      from: AI_BRIDGE_SOURCE.ASSISTANT,
    };
  }
  if (typeof payload !== 'object') {
    return null;
  }
  const raw = payload as Record<string, unknown>;
  const pathCandidate =
    (typeof raw.path === 'string' && raw.path.trim()) ||
    (typeof raw.content === 'string' && raw.content.trim()) ||
    '';
  if (!pathCandidate) {
    return null;
  }
  const params =
    raw.params && typeof raw.params === 'object' && !Array.isArray(raw.params)
      ? (raw.params as Record<string, string>)
      : undefined;
  return {
    path: pathCandidate,
    content: typeof raw.content === 'string' ? raw.content : pathCandidate,
    from: typeof raw.from === 'string' ? raw.from : undefined,
    ts: typeof raw.ts === 'number' ? raw.ts : undefined,
    action: typeof raw.action === 'string' ? raw.action : undefined,
    params,
  };
}

/**
 * 合并 path 自带 query 与 params：path 已有 key 优先，params 只补缺。
 * 例：path=?foo=1 + params={foo:'2',id:'3'} → ?foo=1&id=3
 */
export function buildRoutePath(payload: AiRoutePayload): string {
  const rawPath = payload.path || '';
  const qIndex = rawPath.indexOf('?');
  const pathname = qIndex >= 0 ? rawPath.slice(0, qIndex) : rawPath;
  const search = qIndex >= 0 ? rawPath.slice(qIndex + 1) : '';

  const merged = new URLSearchParams(search);
  if (payload.params) {
    Object.keys(payload.params).forEach((key) => {
      if (!merged.has(key)) {
        const value = payload.params![key];
        if (value != null && value !== '') {
          merged.set(key, String(value));
        }
      }
    });
  }

  const query = merged.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function isValidRoutePath(path: string): boolean {
  return typeof path === 'string' && path.startsWith('/');
}

/**
 * 读取 iframe 当前业务内部 path（与 ROUTE path 同形态：pathname + search）。
 * 本仓为 Hash 路由：业务段在 hash 内（如 `#/recruit/approvalall?tab=phone`）。
 */
export function getIframeInnerPath(): string {
  const hash = window.location.hash || '';
  if (hash.startsWith('#')) {
    const inner = hash.slice(1);
    if (inner.startsWith('/')) {
      return inner;
    }
  }
  return `${window.location.pathname || ''}${window.location.search || ''}`;
}

/** 由内部 path 拆出 pathname / search（search 含前导 `?`，无 query 则为 `''`） */
export function splitInnerPath(path: string): { pathname: string; search: string } {
  const qIndex = path.indexOf('?');
  if (qIndex < 0) {
    return { pathname: path, search: '' };
  }
  return {
    pathname: path.slice(0, qIndex),
    search: `?${path.slice(qIndex + 1)}`,
  };
}

/** 组装 ROUTE_STATE payload */
export function buildRouteStatePayload(
  path: string,
  requestId?: string
): AiRouteStatePayload {
  const { pathname, search } = splitInnerPath(path);
  return {
    path,
    pathname,
    search,
    href: window.location.href,
    requestId,
    at: Date.now(),
  };
}

/**
 * 从 ACTION payload 解析规范化操作对象。
 * 无有效 action 时返回 null。
 */
export function parseActionPayload(payload: unknown): AiActionPayload | null {
  if (payload == null) {
    return null;
  }
  if (typeof payload === 'string') {
    const action = payload.trim();
    if (!action) {
      return null;
    }
    return {
      action,
      from: AI_BRIDGE_SOURCE.ASSISTANT,
    };
  }
  if (typeof payload !== 'object') {
    return null;
  }
  const raw = payload as Record<string, unknown>;
  const action = typeof raw.action === 'string' ? raw.action.trim() : '';
  if (!action) {
    return null;
  }
  const params =
    raw.params && typeof raw.params === 'object' && !Array.isArray(raw.params)
      ? (raw.params as Record<string, unknown>)
      : undefined;
  return {
    action,
    params,
    from: typeof raw.from === 'string' ? raw.from : undefined,
    ts: typeof raw.ts === 'number' ? raw.ts : undefined,
  };
}
